import { InboxOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd/es/upload';
import Papa from 'papaparse';
import { useMemo, useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { handleAPIError } from '../../utils/helpers';

const { Dragger } = Upload;
const { Title, Text } = Typography;

interface UploadedRow {
	imei: string;
	model: string;
	device_user: string;
	last_seen: string;
}

interface MatchResult {
	imei: string;
	found: boolean;
	is_completed: boolean;
	status: string | null;
	user_name: string | null;
	phone_number: string | null;
	phone_model: string | null;
	device_id: number | null;
}

interface Row extends UploadedRow {
	match?: MatchResult;
}

const ForRelease = () => {
	const [uploaded, setUploaded] = useState<UploadedRow[]>([]);
	const [matches, setMatches] = useState<Record<string, MatchResult>>({});
	const [fileName, setFileName] = useState<string | null>(null);

	const matchMutation = useMutation({
		mutationFn: async (imeis: string[]) => {
			const response = await axios.post('/admin/super/devices/match-imeis', { imeis }, { withCredentials: true });
			return response.data.results.results as MatchResult[];
		},
		onSuccess: (results) => {
			const byImei: Record<string, MatchResult> = {};
			results.forEach((r) => {
				byImei[r.imei] = r;
			});
			setMatches(byImei);
			const completedCount = results.filter((r) => r.is_completed).length;
			message.success(`Matched ${results.length} IMEIs — ${completedCount} completed and ready for release`);
		},
		onError: handleAPIError,
	});

	const parseCsv = (file: File) => {
		Papa.parse<Record<string, string>>(file, {
			header: true,
			skipEmptyLines: true,
			complete: (parsed) => {
				const rows: UploadedRow[] = parsed.data
					.map((row) => ({
						imei: (row['IMEI Number'] || '').trim(),
						model: (row['Model'] || '').trim(),
						device_user: (row['Device User'] || '').trim(),
						last_seen: (row['Last Seen Time'] || '').trim(),
					}))
					.filter((r) => r.imei && /^\d+$/.test(r.imei));

				if (rows.length === 0) {
					message.error('No valid IMEI Number rows found. Expected column: "IMEI Number".');
					return;
				}

				setUploaded(rows);
				setFileName(file.name);
				setMatches({});
				matchMutation.mutate(rows.map((r) => r.imei));
			},
			error: () => message.error('Failed to parse CSV file'),
		});
	};

	const uploadProps: UploadProps = {
		accept: '.csv',
		multiple: false,
		beforeUpload: (file) => {
			parseCsv(file);
			return false; // prevent auto-upload
		},
		showUploadList: false,
	};

	const rows: Row[] = useMemo(
		() => uploaded.map((r) => ({ ...r, match: matches[r.imei] })),
		[uploaded, matches],
	);

	const releaseRows = useMemo(
		() => rows.filter((r) => r.match?.is_completed || (r.match && !r.match.found)),
		[rows],
	);

	const summary = useMemo(() => {
		const total = rows.length;
		const completed = rows.filter((r) => r.match?.is_completed).length;
		const active = rows.filter((r) => r.match?.found && !r.match.is_completed).length;
		const notFound = rows.filter((r) => r.match && !r.match.found).length;
		return { total, completed, active, notFound };
	}, [rows]);

	const handleExport = () => {
		if (releaseRows.length === 0) {
			message.warning('No devices to export');
			return;
		}
		const csv = Papa.unparse(
			releaseRows.map((r) => ({
				IMEI: r.imei,
				Model: r.model,
				Reason: r.match?.is_completed ? 'Completed' : 'Not in Zazu',
				Customer: r.match?.user_name || '',
				Phone: r.match?.phone_number || '',
				'CSV Device User': r.device_user,
			})),
		);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `for-release-${new Date().toISOString().split('T')[0]}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleReset = () => {
		setUploaded([]);
		setMatches({});
		setFileName(null);
	};

	const columns: ColumnsType<Row> = [
		{
			title: 'IMEI',
			dataIndex: 'imei',
			key: 'imei',
			render: (imei: string) => <span className="font-mono text-xs">{imei}</span>,
		},
		{
			title: 'Model (CSV)',
			dataIndex: 'model',
			key: 'model',
		},
		{
			title: 'Zazu Match',
			key: 'match',
			render: (_, row) => {
				const m = row.match;
				if (!m) return <Tag>Checking…</Tag>;
				if (!m.found) return <Tag color="default">Not in Zazu</Tag>;
				if (m.is_completed) return <Tag color="green">Completed — Release</Tag>;
				return <Tag color="blue">Active ({m.status})</Tag>;
			},
		},
		{
			title: 'Customer (Zazu)',
			key: 'customer',
			render: (_, row) => {
				const m = row.match;
				if (!m?.found) return '—';
				return (
					<div className="text-xs">
						<div>{m.user_name}</div>
						<div className="text-gray-500">{m.phone_number}</div>
					</div>
				);
			},
		},
	];

	return (
		<AppLayout
			title="For Release"
			subtitle="Upload the FieldX device list CSV to identify completed devices that should be released."
		>
			{uploaded.length === 0 ? (
				<Card className="shadow-sm">
					<Dragger {...uploadProps} className="p-6">
						<p className="ant-upload-drag-icon">
							<InboxOutlined />
						</p>
						<p className="ant-upload-text">Click or drag CSV file to upload</p>
						<p className="ant-upload-hint">Expected columns: IMEI Number, Model, Device User, Last Seen Time</p>
					</Dragger>
				</Card>
			) : (
				<>
					<Card className="mb-4 shadow-sm">
						<div className="flex flex-wrap items-center justify-between gap-4">
							<div>
								<Text type="secondary">Uploaded file</Text>
								<Title level={5} style={{ margin: 0 }}>
									{fileName}
								</Title>
							</div>
							<Space size="large">
								<div>
									<Text type="secondary" className="text-xs">Total rows</Text>
									<div className="text-xl font-semibold">{summary.total}</div>
								</div>
								<div>
									<Text type="secondary" className="text-xs">For release</Text>
									<div className="text-xl font-semibold text-green-600">{summary.completed}</div>
								</div>
								<div>
									<Text type="secondary" className="text-xs">Active</Text>
									<div className="text-xl font-semibold text-blue-600">{summary.active}</div>
								</div>
								<div>
									<Text type="secondary" className="text-xs">Not in Zazu</Text>
									<div className="text-xl font-semibold text-gray-500">{summary.notFound}</div>
								</div>
							</Space>
							<Space>
								<Button icon={<DownloadOutlined />} type="primary" onClick={handleExport} disabled={releaseRows.length === 0}>
									Export For Release ({releaseRows.length})
								</Button>
								<Button icon={<ReloadOutlined />} onClick={handleReset}>
									Upload New File
								</Button>
							</Space>
						</div>
					</Card>

					{summary.completed > 0 && (
						<Alert
							className="mb-4"
							type="success"
							showIcon
							message={`${summary.completed} device(s) are completed on Zazu — release them from FieldX MDM.`}
						/>
					)}

					<div className="mb-20">
						<Table
							columns={columns}
							dataSource={rows}
							rowKey="imei"
							loading={matchMutation.isPending}
							rowClassName={(row) => (row.match?.is_completed ? 'bg-green-50' : '')}
							pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }}
						/>
					</div>
				</>
			)}
		</AppLayout>
	);
};

export default ForRelease;
